import { useState } from 'react';
import { Copy, Info } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import type { BulkCreateMachineRequest, WizardData } from '../../types/machine-setup-types';

interface BulkCreateStepProps {
  /** The original wizard data from the first machine creation. */
  wizardData: Partial<WizardData>;
  /** Called when the user clicks "Create {count} machines". */
  onSubmit: (request: BulkCreateMachineRequest) => void;
  onBack: () => void;
  isLoading: boolean;
}

export function BulkCreateStep({ wizardData, onSubmit, onBack, isLoading }: BulkCreateStepProps) {
  const [baseName, setBaseName] = useState(wizardData.machineName?.trim() ?? '');
  const [count, setCount]       = useState(1);

  const safeCount = Math.max(1, Math.min(50, count));
  const preview   = Array.from({ length: safeCount }, (_, i) => `${baseName.trim() || 'Machine'} ${i + 2}`);

  const handleSubmit = () => {
    onSubmit({
      baseName:               baseName.trim(),
      count:                  safeCount,
      machineName:            baseName.trim(),
      machineTypeKey:         wizardData.machineTypeKey ?? '',
      actorId:                wizardData.actorId ?? 0,
      machineGroupId:         wizardData.machineGroupId,
      defaultCredit:          wizardData.defaultCredit,
      maxCurrentEvse1:        wizardData.maxCurrentEvse1,
      maxCurrentEvse2:        wizardData.maxCurrentEvse2,
      tariffId:               wizardData.tariffId,
      defaultLanguage:        wizardData.defaultLanguage,
      chargerMaxCurrent:      wizardData.chargerMaxCurrent,
      connectorFormat1:       wizardData.connectorFormat1,
      socketType1:            wizardData.socketType1,
      noOfPhases1:            wizardData.noOfPhases1,
      chargingType1:          wizardData.chargingType1,
      maxPowerKw1:            wizardData.maxPowerKw1,
      minCurrentEvse1:        wizardData.minCurrentEvse1,
      connectorFormat2:       wizardData.connectorFormat2,
      socketType2:            wizardData.socketType2,
      noOfPhases2:            wizardData.noOfPhases2,
      chargingType2:          wizardData.chargingType2,
      maxPowerKw2:            wizardData.maxPowerKw2,
      minCurrentEvse2:        wizardData.minCurrentEvse2,
      customAttributeValues:  wizardData.customAttributeValues,
    });
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Create More Machines</h2>
      <p className="text-sm text-gray-500 mb-6">
        All settings from the previous machine will be reused. No device will be assigned.
      </p>

      {/* Info banner */}
      <div className="flex items-start gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-4 py-3 mb-6">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
        <span>
          These machines will be created without a linked device. You can assign devices later via the
          Lynx portal or the serial validation flow.
        </span>
      </div>

      <div className="space-y-5 max-w-sm">
        {/* Base name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Base name</label>
          <input
            type="text"
            value={baseName}
            onChange={(e) => setBaseName(e.target.value)}
            placeholder="e.g. EV Charger"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="mt-1 text-xs text-gray-400">
            Machines will be named "{baseName.trim() || 'Machine'} 2", "{baseName.trim() || 'Machine'} 3", …
          </p>
        </div>

        {/* Count */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Number of machines to create
            <span className="ml-1.5 text-xs font-normal text-gray-400">(1–50)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={1}
              max={50}
              value={safeCount}
              onChange={(e) => setCount(Number(e.target.value))}
              className="flex-1 accent-primary"
            />
            <input
              type="number"
              min={1}
              max={50}
              value={safeCount}
              onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value))))}
              className="w-16 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Preview */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Preview ({safeCount} machine{safeCount !== 1 ? 's' : ''})</p>
          <ul className="rounded-md border border-gray-200 bg-gray-50 divide-y divide-gray-100 max-h-40 overflow-y-auto text-sm">
            {preview.slice(0, 6).map((name) => (
              <li key={name} className="flex items-center gap-2 px-3 py-1.5">
                <Copy className="h-3 w-3 text-gray-400 shrink-0" />
                <span className="text-gray-700">{name}</span>
              </li>
            ))}
            {preview.length > 6 && (
              <li className="px-3 py-1.5 text-gray-400 text-xs">… and {preview.length - 6} more</li>
            )}
          </ul>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-8">
        <Button variant="outline" onClick={onBack} disabled={isLoading}>
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading || !baseName.trim()}>
          {isLoading ? 'Creating…' : `Create ${safeCount} machine${safeCount !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}
